import {
  Injectable,
  Inject,
  PLATFORM_ID,
  signal,
  computed,
  inject,
  effect,
  untracked,
} from '@angular/core';
import { Socket, io } from 'socket.io-client';
import { AuthService } from './auth.service';
import { ToastService } from '../../shared/toast-alert/toast.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';
import { PositionAlertService } from './position-alert.service';
import { Position } from '../models';
import { isPlatformBrowser } from '@angular/common';

export type PatrolAlertStatus = 'all' | 'active' | 'resolved' | 'ignored';
export interface PatrolAlert {
  id: string;
  title: string;
  description: string;
  type: 'no_movement' | 'patrol_delay' | 'patrol_missing';
  status: 'active' | 'resolved' | 'ignored';
  position_id?: number;
  position: Position;
  position_name?: string;
  company_id?: number;
  last_patrol_time?: Date;
  duration_minutes: number;
  created_at: Date;
  resolved_at?: Date;
  metadata?: any;
}

@Injectable({
  providedIn: 'root',
})
export class PatrolMonitorService {
  private socket: Socket | null = null;
  private platformId = inject(PLATFORM_ID);

  // Signals para gerenciar estado
  private alerts = signal<PatrolAlert[]>([]);
  private newAlertSignal = signal<PatrolAlert | null>(null);
  private isConnectedSignal = signal<boolean>(false);
  private hasUnreadSignal = signal<boolean>(false);
  private readonly ALERT_DISPLAY_INTERVAL = 260 * 1000; // segundos * milessegundos;

  // Fila de alertas para exibição sequencial
  private alertQueue: PatrolAlert[] = [];
  private isProcessingQueue = false;
  private queueCleanup: (() => void) | null = null;

  private openDropdownSubject = new BehaviorSubject<boolean>(false);
  openDropdown$ = this.openDropdownSubject.asObservable();

  // Computed para alertas ativos
  activeAlerts = computed(() => {
    return this.alerts().filter((a) => a.status === 'active');
  });

  alertCount = computed(() => {
    return this.activeAlerts().length;
  });

  showBadge = computed(() => {
    return this.alertCount() > 0 || this.hasUnreadSignal();
  });

  alerts$ = this.alerts.asReadonly();
  newAlert$ = this.newAlertSignal.asReadonly();
  isConnected$ = this.isConnectedSignal.asReadonly();
  hasUnread$ = this.hasUnreadSignal.asReadonly();

  private positionAlertService = inject(PositionAlertService);

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
  ) {
    // Configura um effect para detectar quando o modal é fechado
    // effect(() => {
    //   const isOpen = this.positionAlertService.isOpen();
    //   // Se o modal foi fechado e estamos processando fila
    //   if (!isOpen && this.isProcessingQueue) {
    //     // Limpa qualquer timer pendente e continua a fila
    //     this.continueQueueProcessing();
    //   }
    // });
  }

  connect(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.socket && this.socket.connected) {
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      console.warn('Sem token, não é possível conectar');
      return;
    }

    const socketUrl = environment.socketUrl;

    this.socket = io(`${socketUrl}/patrol-monitor`, {
      auth: { token },
      transports: ['websocket'],
    });

    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnectedSignal.set(true);
      this.getActiveAlerts();
    });

    this.socket.on('disconnect', () => {
      console.warn('Desconectado do Patrol Monitor WebSocket');
      this.isConnectedSignal.set(false);
    });

    this.socket.on('patrol_alert', (data: any) => {
      const alert = data.data || data;

      // this.alerts.update((current)=> [alert,...current]);
      this.alerts.update((current) => {
        const exists = current.some((a) => a.title === alert.title);
        if (exists) {
          return current.map((a) => (a.title === alert.title ? { ...a, ...alert } : a));
        }
        return [alert, ...current];
      });

      this.newAlertSignal.set(alert);
      this.hasUnreadSignal.set(true);

      // Adiciona à fila para processamento sequencial
      this.addAlertToQueue(alert);

      // Toca som
      // this.playAlertSound();

      // this.openDropdownSubject.next(true);
    });

    this.socket.on('active_alerts', (alerts: PatrolAlert[]) => {
      this.alerts.set(alerts);
    });

    this.socket.on('alert_resolved', (data: { alertId: string }) => {
      this.alerts.update((current) =>
        current.map((a) =>
          a.id === data.alertId ? { ...a, status: 'resolved', resolved_at: new Date() } : a,
        ),
      );
    });

    this.socket.on('error', (error: any) => {
      console.error('Erro no WebSocket:', error);
    });
  }

  /**
   * Adiciona alerta à fila para exibição sequencial
   */
  private addAlertToQueue(alert: PatrolAlert): void {
    const existingIndex = this.alertQueue.findIndex((a) => a.position_id === alert.position_id);

    if (existingIndex !== -1) {
      // A EXISTENTE PELO MAIS NOVO
      this.alertQueue[existingIndex] = alert;
      console.log(`Alerta actualizado para o posto ${alert.position_id} `);
      return;
    }
    this.alertQueue.push(alert);

    // Se não estiver processando, inicia o processamento
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }
  private waitBetweenAlerts(delay: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, delay);
    });
  }
  /**
   * Processa a fila de alertas sequencialmente
   */
  private async processQueue(): Promise<void> {
    if (this.alertQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    if (this.isProcessingQueue) return;

    this.isProcessingQueue = true;

    while (this.alertQueue.length > 0) {
      const alert = this.alertQueue.shift();

      if (!alert) continue;

      const isStillActive = this.alerts().some((a) => a.id === alert.id && a.status === 'active');

      if (!isStillActive) {
        console.log(`Alerta ${alert.id} já foi resolvido, pulando...`);
        continue;
      }
      // if (alert) {
      this.positionAlertService.openModal(alert);
      this.playAlertSound(); //play

      await this.waitForModalClose(15000);

      if (this.alertQueue.length > 0) {
        console.log(
          `Aguardando ${this.ALERT_DISPLAY_INTERVAL / 600} minutos antes do próximo alerta...`,
        );
        await this.waitBetweenAlerts(this.ALERT_DISPLAY_INTERVAL);
      }
      // }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Continua o processamento da fila após fechamento manual
   */
  private continueQueueProcessing(): void {
    // Se ainda há alertas na fila, continua o processamento
    if (this.alertQueue.length > 0 && this.isProcessingQueue) {
      // Pequeno delay para garantir que o modal foi fechado
      setTimeout(() => {
        this.processQueue();
      }, 300);
    } else if (this.alertQueue.length === 0) {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Aguarda o fechamento do modal ou timeout
   */
  private waitForModalClose(timeout: number): Promise<void> {
    return new Promise((resolve) => {
      let resolved = false;

      // 1. Timeout para fechamento automático do modal por inatividade
      const timer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.positionAlertService.closeModal();
          cleanup();
          resolve();
        }
      }, timeout);

      // 2. Se o seu PositionAlertService tiver um Observable para quando o modal fecha,
      // use-o aqui. Como alternativa segura, criamos um intervalo rápido para checar o sinal.
      const interval = setInterval(() => {
        const isOpen = this.positionAlertService.isOpen();
        if (!isOpen && !resolved) {
          resolved = true;
          cleanup();
          resolve();
        }
      }, 250); // Checa a cada 250ms se o modal foi fechado pelo usuário

      // 3. Listener para quando o alerta for resolvido via botão de ação
      const resolveSub = this.positionAlertService.alertResolved$.subscribe(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve();
        }
      });

      // Função centralizada para limpar os listeners e evitar vazamento de memória
      const cleanup = () => {
        clearTimeout(timer);
        clearInterval(interval);
        resolveSub.unsubscribe();
      };
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnectedSignal.set(false);
    }
    // Limpa a fila
    this.alertQueue = [];
    this.isProcessingQueue = false;
    if (this.queueCleanup) {
      this.queueCleanup();
      this.queueCleanup = null;
    }
  }

  getActiveAlerts(): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('get_active_alerts');
    }
  }

  resolveAlert(alertId: string): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('resolve_alert', { alertId });
    } else {
      console.warn('WebSocket não conectado para resolver alerta');
    }
  }

  markAsRead(): void {
    this.hasUnreadSignal.set(false);
  }

  /**
   * Toca som de alerta com fallback para diferentes navegadores
   */
  public playAlertSound(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const audio = new Audio('/alert.mp3');
      audio.volume = 0.5;

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn('Erro ao reproduzir som:', error);
          this.playSoundFallback();
        });
      }
    } catch (error) {
      console.warn('Erro ao reproduzir som:', error);
      this.playSoundFallback();
    }
  }

  /**
   * Fallback para tocar som usando Web Audio API
   */
  private playSoundFallback(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return;

      const audioContext = new AudioContext();

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);

      setTimeout(() => {
        try {
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 1000;
          osc2.type = 'sine';
          gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          osc2.start();
          osc2.stop(audioContext.currentTime + 0.3);
        } catch (e) {
          // Ignora erro no segundo beep
        }
      }, 200);
    } catch (error) {
      console.warn('Erro no fallback de áudio:', error);
    }
  }

  private showToastNotification(alert: PatrolAlert): void {
    this.toastService.warning(alert.description, `${alert.title}`, {
      duration: 10000,
      position: 'top-right',
      onClick: () => {
        this.openDropdownSubject.next(true);
      },
      action: {
        label: 'Ver alerta',
        onClick: () => {
          this.openDropdownSubject.next(true);
        },
      },
    });
  }

  getAlertIcon(type: string): string {
    const icons: Record<string, string> = {
      no_movement: '🚨',
      patrol_delay: '⚠️',
      patrol_missing: '🔴',
    };
    return icons[type];
  }

  getAlertColor(type: string): string {
    const colors: Record<string, string> = {
      no_movement: 'red',
      patrol_delay: 'orange',
      patrol_missing: 'red',
    };
    return colors[type] || 'blue';
  }

  formatAlertTime(date: Date): string {
    if (!date) return '';
    const diff = new Date().getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}min atrás`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h atrás`;
    return `${hours}h ${remainingMinutes}min atrás`;
  }

  getAlertTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      no_movement: 'Sem Movimento',
      patrol_delay: 'Atraso na Patrulha',
      patrol_missing: 'Patrulha Ausente',
    };
    return labels[type] || type;
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      active: 'Activo',
      resolved: 'Resolvido',
      ignored: 'Ignorado',
    };
    return labels[status] || status;
  }
}
